import React, { useCallback, useEffect, useState } from 'react';
import { useP } from '../../i18n/useP';
import CardShell from '../../components/CardShell';
import { Cell, Choix } from '../../components/InlineCells';
import { getBsView } from '../../api/financials_bs';
import { listLines } from '../../api/financials_pl';
import { getForecastAssumptions, putForecastAssumption } from '../../api/forecastAssumptions';
import Listbox from '../../components/Listbox';
import { accountOptions } from './accountNature';
import './CashMoves.css';

/* AUTRES MOUVEMENTS du plan de liquidité (décision Olivier, 2026-09-27), UNE
   carte depuis le 2026-10-20 : « Opération avec impact sur la liquidité », où l'on ajoute
   autant de lignes qu'on veut. Une ligne = un libellé et des francs par mois
   ouvert ; la colonne Sens dit encaissement ou décaissement, et une
   CONTREPARTIE facultative l'envoie à sa place : un poste du bilan, ou un compte
   de charges ou de produits hors exploitation. Sans contrepartie, ce que la
   ligne fait bouger se lit en « Autres variations de prévision », jamais
   absorbé dans la trésorerie. La carte sœur, sans impact sur la liquidité,
   vit dans `SpecialEntriesCard` ; les deux se lisent dans Opérations
   spéciales, celle-ci reste aussi un détail du Plan de liquidité. */

const MONTHS_ABBR = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];

export default function CashMovesPage({ activeTenant, year, unit = 'kchf', canWrite = true, closeAction = null }) {
  const p = useP();
  const tid = activeTenant?.id;
  const hasFin = (activeTenant?.enabled_modules || []).includes('financials');
  const [closed, setClosed] = useState(null);
  const [moves, setMoves] = useState([]);
  const [counterLines, setCounterLines] = useState({ bs: [], pl: [] });   // contreparties possibles
  const div = unit === 'chf' ? 1 : 1000;

  const load = useCallback(() => {
    if (!tid) { setClosed(null); setMoves([]); return; }
    Promise.all([
      getBsView(tid, year, { flatForecast: true }).catch(() => null),
      listLines(tid).catch(() => null),
    ]).then(([rb, rp]) => {
      const bsv = rb && !rb.error ? rb.data : null;
      setClosed(Number(bsv?.closed_count) || 0);
      /* LA CONTREPARTIE (demande d'Olivier, 2026-09-27) : un poste du bilan
         (la liquidité exceptée, c'est elle qui bouge) ou une ligne du compte de
         résultat, d'exploitation comprise depuis le 2026-10-20 : une vente
         encaissée pose son produit. */
      setCounterLines({
        bs: (bsv?.lines || []).filter((l) => !l.hidden && l.std_key !== 'liquidity'),
        pl: (Array.isArray(rp?.data) ? rp.data : []).filter((l) => !l.hidden),
      });
    });
    getForecastAssumptions(tid, year).then((r) => {
      const v = r.error ? null : r.data?.cash_moves;
      setMoves(Array.isArray(v) ? v.filter((mv) => mv && (mv.side === 'in' || mv.side === 'out')) : []);
    }).catch(() => setMoves([]));
  }, [tid, year]);
  useEffect(() => { load(); }, [load]);

  const save = (next) => {
    setMoves(next);
    if (tid) putForecastAssumption(tid, year, 'cash_moves', next).catch(() => {});
  };

  if (!tid) return <p className="cmv-empty">{p("Sélectionne d'abord une entreprise.")}</p>;
  if (closed == null) return <p className="cmv-empty">{p('Chargement…')}</p>;

  const counters = accountOptions(counterLines.pl, counterLines.bs, p);
  const months = [];
  for (let m = closed + 1; m <= 12; m += 1) months.push(m);

  const add = () => save([...moves, { id: `mv_${Math.random().toString(36).slice(2, 9)}`, label: '', side: 'in', months: {} }]);
  const patch = (id, fn) => save(moves.map((mv) => (mv.id === id ? fn(mv) : mv)));
  const del = (id) => save(moves.filter((mv) => mv.id !== id));
  const fmtU = (chf) => (chf == null || Math.abs(Number(chf)) < 0.5 ? '' : Math.round(Number(chf) / div).toLocaleString('de-CH'));
  const money = (chf) => ({
    value: chf == null ? null : Math.round(Number(chf) / div), type: 'number',
    format: (x) => (x == null || Number(x) === 0 ? '' : Number(x).toLocaleString('de-CH')),
    toDraft: (x) => (x == null || Number(x) === 0 ? '' : String(x)),
  });
  const SIDES = [{ value: 'in', label: p('Encaissement') }, { value: 'out', label: p('Décaissement') }];
  /* Un encaissement compte en plus, un décaissement en moins : la ligne de
     total est le solde que le plan reçoit de ces mouvements. */
  const signed = (mv, m) => (mv.side === 'out' ? -1 : 1) * (Number(mv.months?.[String(m)]) || 0);
  const total = {};
  months.forEach((m) => { total[m] = moves.reduce((s2, mv) => s2 + signed(mv, m), 0); });
  const sumOf = (mv) => months.reduce((s2, m) => s2 + (Number(mv.months?.[String(m)]) || 0), 0);
  const sumSigned = (mv) => (mv.side === 'out' ? -1 : 1) * sumOf(mv);

  return (
    <div className="cmv">
      {/* Le bouton qui referme le détail vit dans l'ENTÊTE de la carte, avec
          l'ajout de ligne : jamais dans une barre au-dessus. */}
      <CardShell
        className="report-card cmv-card"
        title={p('Opération avec impact sur la liquidité')}
        primaryAction={(canWrite || closeAction) ? (
          <>
            {canWrite && <button type="button" className="lk-btn" onClick={add}>＋ {p('Ligne')}</button>}
            {closeAction}
          </>
        ) : null}
        flush
      >
        <div className="lk-hscroll">
          <table className="lk-entry-table cmv-tbl">
            <colgroup>
              <col style={{ width: 220 }} />
              <col style={{ width: 130 }} />
              <col style={{ width: 220 }} />
              {months.map((m) => <col key={m} style={{ width: 76 }} />)}
              <col style={{ width: 90 }} />
              <col style={{ width: 30 }} />
            </colgroup>
            <thead>
              <tr>
                <th>{p('Libellé')}</th>
                <th>{p('Sens')}</th>
                <th>{p('Contrepartie')}</th>
                {months.map((m) => <th key={m} className="lk-num">{MONTHS_ABBR[m - 1]}</th>)}
                <th className="lk-num">{p('Total')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {moves.map((mv) => (
                <tr key={mv.id}>
                  <td>
                    <Cell value={mv.label || ''} canWrite={canWrite} placeholder={p('libellé')}
                          onSave={(v) => patch(mv.id, (x) => ({ ...x, label: v }))} />
                  </td>
                  <td>
                    <Choix value={mv.side} canWrite={canWrite} options={SIDES}
                           onSave={(v) => patch(mv.id, (x) => ({ ...x, side: v }))} />
                  </td>
                  <td>
                    {canWrite && hasFin ? (
                      <Listbox className="cmv-lb" value={mv.counter || ''} searchable
                               options={[{ value: '', label: p('sans contrepartie') }, ...counters]}
                               searchPlaceholder={p('Rechercher un compte…')}
                               onChange={(v) => patch(mv.id, (x) => ({ ...x, counter: v || null }))} />
                    ) : (
                      <Choix value={mv.counter || ''} canWrite={false}
                             options={[{ value: '', label: p('sans contrepartie') }, ...counters.filter((o) => !o.group)]}
                             onSave={() => {}} />
                    )}
                  </td>
                  {months.map((m) => (
                    <td key={m} className="lk-num">
                      <Cell {...money(mv.months?.[String(m)])} canWrite={canWrite} placeholder="0"
                            onSave={(v) => patch(mv.id, (x) => {
                              const next = { ...(x.months || {}) };
                              if (v == null || Number(v) === 0) delete next[String(m)];
                              else next[String(m)] = Math.abs(v) * div;
                              return { ...x, months: next };
                            })} />
                    </td>
                  ))}
                  <td className="lk-num">{fmtU(sumSigned(mv))}</td>
                  <td>
                    {canWrite && (
                      <button type="button" className="lk-x lk-del" aria-label={p('Supprimer')} onClick={() => del(mv.id)}>✕</button>
                    )}
                  </td>
                </tr>
              ))}
              {moves.length > 0 && (
                <tr className="lk-hi-strong">
                  <td>{p('Total')}</td>
                  <td />
                  <td />
                  {months.map((m) => <td key={m} className="lk-num">{fmtU(total[m])}</td>)}
                  <td className="lk-num">{fmtU(months.reduce((s2, m) => s2 + total[m], 0))}</td>
                  <td />
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </CardShell>
    </div>
  );
}
